import { createContext, useContext, useState, ReactNode, useCallback } from "react";

interface PlaybackTimeContextType {
  currentTimeSeconds: number | undefined;
  setCurrentTimeSeconds: (time: number | undefined) => void;
  seekToTime: (timeSeconds: number) => void;
  registerSeekHandler: (handler: (timeSeconds: number) => void) => () => void;
}

const PlaybackTimeContext = createContext<PlaybackTimeContextType | undefined>(
  undefined
);

export function PlaybackTimeProvider({ children }: { children: ReactNode }) {
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState<
    number | undefined
  >();
  const [seekHandler, setSeekHandler] = useState<
    ((timeSeconds: number) => void) | undefined
  >();

  const registerSeekHandler = useCallback((handler: (timeSeconds: number) => void) => {
    setSeekHandler(() => handler);
    // Return cleanup function
    return () => {
      setSeekHandler(undefined);
    };
  }, []);

  const seekToTime = useCallback((timeSeconds: number) => {
    if (seekHandler) {
      seekHandler(timeSeconds);
    }
  }, [seekHandler]);

  return (
    <PlaybackTimeContext.Provider
      value={{ currentTimeSeconds, setCurrentTimeSeconds, seekToTime, registerSeekHandler }}
    >
      {children}
    </PlaybackTimeContext.Provider>
  );
}

export function usePlaybackTime() {
  const context = useContext(PlaybackTimeContext);
  if (context === undefined) {
    throw new Error(
      "usePlaybackTime must be used within a PlaybackTimeProvider"
    );
  }
  return context;
}
